import { useState, useEffect } from "react";
import { LocationData } from "../types";
import { LocationService } from "../services/LocationService";

export function useLocation(apiKey: string) {
  const [locationData, setLocationData] = useState<LocationData | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissionState, setPermissionState] =
    useState<PermissionState>("prompt");

  const locationService = LocationService.getInstance(apiKey);

  const getLocationData = async () => {
    try {
      setLoading(true);

      // Check current permission state
      const currentPermission = await locationService.checkLocationPermission();
      setPermissionState(currentPermission);

      // Start fetching IP data immediately in parallel
      const ipDataPromise = locationService.getIpLocation();

      // Handle location based on permission state
      if (currentPermission === "granted" || currentPermission === "prompt") {
        try {
          const position = await locationService.getBrowserLocation();
          console.log("Browser location:", position);
          setLocationData({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            source: "browser",
          });
          return;
        } catch (browserError) {
          console.error("Browser location failed:", browserError);
          // Fall through to use IP data
        }
      }

      // If browser location is denied or fails, use IP data
      const ipData = await ipDataPromise;
      console.log("IP location:", ipData);
      setLocationData({
        latitude: ipData.latitude,
        longitude: ipData.longitude,
        source: "ip",
      });
    } catch (error: any) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  // Function to handle browser location with existing permission
  const getBrowserLocationWithPermission = async () => {
    try {
      const position = await locationService.getBrowserLocation();
      setLocationData({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        source: "browser",
      });
    } catch (error) {
      // If browser location fails, fall back to stored IP data
      console.error("Browser location failed:", error);
      // Maintain existing IP-based location if available
      if (!locationData) {
        const ipData = await locationService.getIpLocation();
        setLocationData({
          latitude: ipData.latitude,
          longitude: ipData.longitude,
          source: "ip",
        });
      }
    }
  };

  // Function to refresh location
  const refreshLocation = async () => {
    const permission = await locationService.checkLocationPermission();

    if (permission === "granted") {
      // If we have permission, just get browser location
      await getBrowserLocationWithPermission();
    } else {
      // If no permission, use IP data
      const ipData = await locationService.getIpLocation();
      setLocationData({
        latitude: ipData.latitude,
        longitude: ipData.longitude,
        source: "ip",
      });
    }
  };

  useEffect(() => {
    getLocationData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    locationData,
    error,
    loading,
    permissionState,
    refreshLocation,
  };
}
